/**
 * Step 0 — Item Definition Setup.
 *
 * Three-mode wizard. Default state shows the mode chooser; once a
 * mode is selected the body swaps to that mode's UI plus an inline
 * mode-switch link at the top-right so the user can flip modes
 * while status is still 'empty'.
 *
 * After successful submission (status flips to 'approved') the body
 * shows a read-only confirmation card and the rail auto-advances
 * to Step 1.
 *
 * Pure dispatcher: receives a grouped `Step0BodyProps` from
 * `useStep0ItemInitiation` and routes based on `selectedMode` +
 * `status`. Owns its own top chrome (mode switch) — StepShell is
 * step-agnostic.
 */
import React from 'react';
import type { Step0BodyProps } from './stepBodyProps';
import type { WorkflowActions } from '../state/workflowV2Types';
import ModeChooserCard from './step0-modes/ModeChooserCard';
import ModeSwitch from './step0-modes/ModeSwitch';
import AutomaticMode from './step0-modes/AutomaticMode';
import ManualMode from './step0-modes/ManualMode';
import DiagramMode from './step0-modes/DiagramMode';
import styles from './Step0ItemInitiation.module.css';

const UPLOAD_STEP_ID = 0;

import { Icon } from '../../../components/ui/Icon';
import { Button } from '../../../components/ui/Button2';
import { getOriginalDocumentUrl } from '../../../api/documents';

const ButtonAny = Button as unknown as React.ComponentType<any>;

/** Read-only confirmation shown after submission succeeds. When the item
 *  definition came from an uploaded document, offer to view the original. */
function ApprovedCard({ fileId }: { fileId?: string | null }) {
  return (
    <section className={styles.section}>
      <div className={styles.approvedHero}>
        <div className={styles.approvedCheck}><Icon name="check" size={72} /></div>
        <h2 className={styles.approvedTitle}>Item definition setup complete</h2>
        <p className={styles.approvedDesc}>
          The analysis agent is processing your input. Continue to Step 1 to review the Item
          Definition once it's ready.
        </p>
        {fileId && (
          <ButtonAny
            variant="outline"
            size="sm"
            startIcon={<Icon name="external-link" size={14} />}
            onClick={() =>
              window.open(getOriginalDocumentUrl(fileId), '_blank', 'noopener,noreferrer')
            }
            title="View the document you uploaded"
          >
            View uploaded document
          </ButtonAny>
        )}
      </div>
    </section>
  );
}

interface Step0Props extends Step0BodyProps {
  actions: WorkflowActions;
}

export default function Step0ItemInitiation({
  status,
  selectedMode,
  upload,
  manual,
  diagram,
  onSelectMode,
  actions,
  fileId,
}: Step0Props) {
  // Approved (post-submission) → read-only confirmation regardless of mode.
  if (status === 'approved') {
    return <ApprovedCard fileId={fileId} />;
  }

  // No mode picked yet → chooser landing.
  if (selectedMode === null) {
    return <ModeChooserCard onSelectMode={onSelectMode} />;
  }

  const body = (() => {
    switch (selectedMode) {
      case 'automatic':
        return <AutomaticMode {...upload} />;
      case 'manual':
        return <ManualMode {...manual} />;
      case 'diagram':
        return <DiagramMode {...diagram} />;
      default:
        return <ModeChooserCard onSelectMode={onSelectMode} />;
    }
  })();

  // ModeSwitch sits inline above the mode body while status is still
  // 'empty'. Once analysis kicks off (generating / approved / failed)
  // switching modes doesn't make sense, so we hide it.
  const showSwitch = status === 'empty';

  return (
    <>
      {showSwitch && (
        <div className={styles.modeBar}>
          <ModeSwitch
            currentMode={selectedMode}
            onSwitch={(mode) => actions.setStepMode(UPLOAD_STEP_ID, mode)}
          />
        </div>
      )}
      {body}
    </>
  );
}
